import { generateId } from "../helpers/generateRandomId";
import { getToken } from "../helpers/utility";

const LOCK_KEY = "_ssell";
const TAB_ID_KEY = "_sse_tab_id";

const LOCK_TTL = 5000;
const LOCK_REFRESH = 2000;
const ELECTION_INTERVAL = 2000;
const RECONNECT_DELAY = 3000;

const SSE_ENDPOINT = `${process.env.REACT_APP_KOTLIN_API_URL}sse/event/export`;

const WORKER_URL = "/sseWorker.js";

class SSEManager {
  static instance = null;

  constructor() {
    this.listeners = new Set();

    this.tabId = this.getTabId();

    this.worker = null;
    this.port = null;

    this.channel = null;

    // Manual SSE connection
    this.sseController = null;
    this.sseReader = null;

    this.isLeader = false;
    this.heartbeat = null;
    this.electionInterval = null;
    this.reconnectTimeout = null;

    this.isDisconnected = false;

    if (typeof window === "undefined") {
      return;
    }

    this.initialize();
  }

  getTabId() {
    try {
      let tabId = sessionStorage.getItem(TAB_ID_KEY);

      if (!tabId) {
        tabId = generateId(8);

        sessionStorage.setItem(
          TAB_ID_KEY,
          tabId
        );
      }

      return tabId;
    } catch (error) {
      return generateId(8);
    }
  }

  static getInstance() {
    if (!SSEManager.instance) {
      SSEManager.instance = new SSEManager();
    }

    return SSEManager.instance;
  }

  static resetInstance() {
    if (!SSEManager.instance) {
      return;
    }

    SSEManager.instance.disconnect();
    SSEManager.instance = null;
  }

  initialize() {
    if ("SharedWorker" in window) {
      this.initSharedWorker();
      return;
    }

    if ("BroadcastChannel" in window) {
      this.initBroadcastLeader();
      return;
    }

    this.startDirectSSE();
  }

  listen(handler) {
    this.listeners.add(handler);

    this.isDisconnected = false;

    if (this.port) {
      this.connectSharedWorker();
    }

    return () => {
      this.listeners.delete(handler);
    };
  }

  emit(payload) {
    this.listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        // Ignore listener errors.
      }
    });
  }

  initSharedWorker() {
    try {
      this.worker = new SharedWorker(WORKER_URL);

      this.port = this.worker.port;

      this.port.onmessage = (event) => {
        const payload = event.data;

        if (!payload) {
          return;
        }

        if (payload.type === "SSE_CONNECTED") {
          console.log("[SSE] Connected");
        }

        if (
          payload.type === "SSE_CONNECTION_ERROR"
        ) {
          console.error("[SSE] Connection error - SSE disconnected due to a network or server problem");
        }

        this.emit(payload);
      };

      this.port.start();
    } catch (error) {
      this.worker = null;
      this.port = null;

      if ("BroadcastChannel" in window) {
        this.initBroadcastLeader();
      } else {
        this.startDirectSSE();
      }
    }
  }

  connectSharedWorker() {
    this.isDisconnected = false;

    if (!this.port) {
      this.initSharedWorker();
    }

    if (!this.port) {
      return;
    }

    const sessionToken = getToken().get("idToken");

    if (!sessionToken) {
      return;
    }

    this.port.postMessage({
      type: "CONNECT",
      tabId: this.tabId,
      sessionToken,
      sseEndpoint: SSE_ENDPOINT,
    });
  }

  initBroadcastLeader() {
    if (this.channel) {
      return;
    }

    this.channel = new BroadcastChannel(
      "app-events"
    );

    this.channel.onmessage = (event) => {
      const {
        payload,
        sender,
      } = event.data || {};

      if (!payload || sender === this.tabId) {
        return;
      }

      this.emit(payload);
    };

    window.addEventListener("storage", this.handleStorageEvent);

    window.addEventListener("beforeunload", this.handleBeforeUnload);

    this.electionInterval =
      window.setInterval(() => {
        if (
          !this.isLeader &&
          this.listeners.size > 0 &&
          !this.isDisconnected
        ) {
          this.tryBecomeLeader();
        }
      }, ELECTION_INTERVAL);

    this.tryBecomeLeader();
  }

  tryBecomeLeader() {
    if (
      this.isDisconnected ||
      this.isLeader ||
      this.listeners.size === 0
    ) {
      return;
    }

    const now = Date.now();
    const lock = this.getLeaderLock();

    if (
      lock &&
      lock.expiresAt > now &&
      lock.tabId !== this.tabId
    ) {
      return;
    }

    const newLock = {
      tabId: this.tabId,
      expiresAt: now + LOCK_TTL,
    };

    localStorage.setItem(LOCK_KEY, JSON.stringify(newLock));

    const verifiedLock = this.getLeaderLock();

    if (verifiedLock && verifiedLock.tabId === this.tabId) {
      this.becomeLeader();
    }
  }

  getLeaderLock() {
    const raw = localStorage.getItem(LOCK_KEY);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  becomeLeader() {
    if (
      this.isLeader ||
      this.isDisconnected ||
      this.listeners.size === 0
    ) {
      return;
    }

    this.isLeader = true;

    this.startHeartbeat();
    this.startSSE();
  }

  startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeat =
      window.setInterval(() => {
        if (!this.isLeader || this.isDisconnected) {
          return;
        }

        localStorage.setItem(
          LOCK_KEY,
          JSON.stringify({
            tabId: this.tabId,
            expiresAt:
              Date.now() + LOCK_TTL,
          })
        );
      }, LOCK_REFRESH);
  }

  stopHeartbeat() {
    if (!this.heartbeat) {
      return;
    }

    clearInterval(this.heartbeat);

    this.heartbeat = null;
  }

  handleStorageEvent = (event) => {
    if (event.key !== LOCK_KEY || !event.newValue) {
      return;
    }

    try {
      const lock = JSON.parse(event.newValue);

      if (
        this.isLeader &&
        lock.tabId !== this.tabId &&
        lock.expiresAt > Date.now()
      ) {

        this.stepDownLeader();
      }
    } catch (error) {
      // Ignore invalid storage events.
    }
  };

  handleBeforeUnload = () => {
    this.stepDownLeader();
  };

  stepDownLeader() {
    if (!this.isLeader) {
      return;
    }

    this.isLeader = false;

    this.closeSSE();
    this.stopHeartbeat();
    this.clearReconnectTimeout();

    const lock = this.getLeaderLock();

    if (lock?.tabId === this.tabId) {
      localStorage.removeItem(LOCK_KEY);
    }
  }

  async startSSE() {
    if (
      this.sseController ||
      !this.isLeader ||
      this.isDisconnected ||
      this.listeners.size === 0
    ) {
      return;
    }

    this.clearReconnectTimeout();

    const sessionToken = getToken().get("idToken");

    if (!sessionToken) {
      return;
    }

    this.sseController = new AbortController();

    try {
      const response = await fetch(
        SSE_ENDPOINT,
        {
          method: "GET",

          headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${sessionToken}`,
            "Cache-Control": "no-cache",
          },

          signal:
            this.sseController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `SSE connection failed: ${response.status} ${response.statusText}`
        );
      }

      if (!response.body) {
        throw new Error(
          "SSE response does not contain a body."
        );
      }

      console.log("[SSE] Connected");

      this.emit({ type: "SSE_CONNECTED" });

      await this.readSSEStream(response.body);

      if (
        this.isLeader &&
        !this.isDisconnected &&
        this.listeners.size > 0
      ) {
        console.log("[SSE] Connection closed by server.");

        this.closeSSE();

        this.scheduleReconnect(() => {
          this.startSSE();
        });
      }
    } catch (error) {

      if (error?.name === "AbortError") {
        console.log("[SSE] Connection aborted.");

        return;
      }

      console.error("[SSE] Connection error", error);

      this.closeSSE();

      this.emit({type: "SSE_CONNECTION_ERROR"});

      if (
        !this.isLeader ||
        this.isDisconnected ||
        this.listeners.size === 0
      ) {
        return;
      }

      this.scheduleReconnect(() => {
        this.startSSE();
      });
    }
  }

  async readSSEStream(body) {
    const reader = body.getReader();

    this.sseReader = reader;

    const decoder = new TextDecoder();

    let buffer = "";

    try {
      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split(/\r?\n\r?\n/);

        buffer = events.pop() || "";

        for (const rawEvent of events) {
          if (!rawEvent.trim()) {
            continue;
          }

          this.processSSEEvent(rawEvent);
        }
      }
    } finally {
      this.sseReader = null;

      try {
        reader.releaseLock();
      } catch (error) {
        // Ignore release errors.
      }
    }
  }

  processSSEEvent(rawEvent) {
    const lines = rawEvent.split(/\r?\n/);

    let eventType = "message";
    let eventId = null;
    let data = "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventType = line.substring(6).trim();
      } else if (
        line.startsWith("id:")
      ) {
        eventId = line.substring(3).trim();
      } else if (
        line.startsWith("data:")
      ) {
        data += line.substring(5).trim() + "\n";
      }
    }

    data = data.trim();

    if (!data) {
      return;
    }

    if (data === "ping") {
      return;
    }

    let payload;

    try {
      payload = JSON.parse(data);
    } catch (error) {
      console.error("[SSE] Failed to parse event", { data, error });

      return;
    }

    const sseEvent = {
      type: "SSE_EVENT",
      eventType,
      eventId,
      data: payload,
    };

    this.emit(sseEvent);

    if (this.channel) {

      this.channel.postMessage({
        payload: sseEvent,
        sender: this.tabId,
      });
    }
  }

  closeSSE() {
    if (this.sseController) {
      console.log("[SSE] Closing connection.");

      this.sseController.abort();

      this.sseController = null;
    }

    this.sseReader = null;
  }

  clearReconnectTimeout() {
    if (!this.reconnectTimeout) {
      return;
    }

    clearTimeout(this.reconnectTimeout);

    this.reconnectTimeout = null;
  }

  scheduleReconnect(callback) {
    if (this.reconnectTimeout) {
      return;
    }

    console.log(`[SSE] Reconnecting in ${RECONNECT_DELAY}ms...`);

    this.reconnectTimeout =
      setTimeout(() => {
        this.reconnectTimeout = null;

        if (
          this.isDisconnected ||
          !this.isLeader ||
          this.listeners.size === 0
        ) {
          return;
        }

        callback();
      }, RECONNECT_DELAY);
  }

  startDirectSSE() {

    if (
      this.eventSource ||
      this.isDisconnected
    ) {
      return;
    }

    this.eventSource = new EventSource(SSE_ENDPOINT);

    this.eventSource.onopen = () => {
      console.log("[SSE] Connected");

      this.emit({ type: "SSE_CONNECTED" });
    };

    this.eventSource.onmessage =
      (event) => {
        this.handleSSEMessage(event);
      };

    this.eventSource.onerror = () => {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }

      this.emit({
        type:
          "SSE_CONNECTION_ERROR",
      });

      if (this.isDisconnected) {
        return;
      }

      this.scheduleReconnect(() => {
        this.startDirectSSE();
      });
    };
  }

  handleSSEMessage(event, shouldBroadcast = false) {
    if (!event?.data) {
      return;
    }

    if (event.data === "ping") {
      return;
    }

    try {
      const payload = JSON.parse(event.data);

      const sseEvent = {
        type: "SSE_EVENT",
        eventType:
          event.type || "message",
        eventId:
          event.lastEventId || null,
        data: payload,
      };

      this.emit(sseEvent);

      if (
        shouldBroadcast &&
        this.channel
      ) {
        this.channel.postMessage({
          payload: sseEvent,
          sender: this.tabId,
        });
      }
    } catch (error) {
      console.error(
        "[SSE] Failed to parse event",
        {
          data: event.data,
          error,
        }
      );
    }
  }

  disconnect(type = "DISCONNECT") {
    this.clearReconnectTimeout();

    this.isDisconnected = true;

    if (this.port) {
      try {
        this.port.postMessage({ type, tabId: this.tabId });
      } catch (error) {
        // Ignore worker disconnect errors.
      }
    }

    this.stepDownLeader();

    this.closeSSE();

    if (this.electionInterval) {
      clearInterval(
        this.electionInterval
      );

      this.electionInterval = null;
    }

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    window.removeEventListener(
      "storage",
      this.handleStorageEvent
    );

    window.removeEventListener(
      "beforeunload",
      this.handleBeforeUnload
    );

    console.log("[SSE] Disconnected");
  }
}

export default SSEManager;